import { useState, useEffect } from 'react';
import { usePlacesWidget } from '../hooks/usePlacesWidget';
import { MapPin, X } from 'lucide-react';
import { formatPlaceLabel } from '../utils/placeUtils';
import { FIELD_AUTH, fieldBorder } from './UI';

interface LocationSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  error?: boolean;
  id?: string;
}

export function LocationSearch({ value, onChange, placeholder, className, required, error, id }: LocationSearchProps) {
  const [inputValue, setInputValue] = useState(value);
  const [hasSelection, setHasSelection] = useState(!!value);

  useEffect(() => {
    setInputValue(value);
    setHasSelection(!!value);
  }, [value]);

  const { ref } = usePlacesWidget({
    onPlaceSelected: (place) => {
      const label = formatPlaceLabel(place) || place.name || '';
      setInputValue(label);
      setHasSelection(true);
      onChange(label);
    },
    options: {
      types: ['geocode'],
      fields: ['address_components', 'name', 'types']
    }
  });

  const handleBlur = () => {
    // Accept whatever the user typed — don't require a formal autocomplete selection
    if (!hasSelection && inputValue.trim()) {
      setHasSelection(true);
      onChange(inputValue.trim());
    }
  };

  return (
    <div className={`relative ${className}`}>
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-app-sub opacity-30" />
      <input
        id={id}
        ref={ref as any}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setHasSelection(false);
        }}
        onBlur={handleBlur}
        placeholder={placeholder || "Search"}
        className={`${FIELD_AUTH} pl-11 pr-10 ${fieldBorder(error, true)} text-app-text`}
      />
      {inputValue && (
        <button aria-label="Clear"
          type="button"
          onClick={() => {
            setInputValue('');
            setHasSelection(false);
            onChange('');
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-app-sub opacity-30 hover:opacity-100 transition-opacity"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
